import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
// Zod schemas for validation
export const columnSchema = z.object({
    name: z.string(),
    type: z.enum(['string', 'number', 'integer', 'boolean', 'date', 'datetime']),
    values: z.array(z.union([z.string(), z.number(), z.boolean()])).optional(),
    suggested: z.boolean(),
});
export const createTableRequestSchema = z.object({
    originalInput: z.string().trim().min(1, 'Table description is required'),
    tableName: z.string().optional(),
});
export const createTableResponseSchema = z.object({
    id: z.string(),
    tableName: z.string().nullable(),
    originalInput: z.string(),
    currentStep: z.string(),
    status: z.enum(['pending', 'processing', 'completed', 'failed']),
    createdAt: z.string(),
});
export const selectColumnsRequestSchema = z.object({
    chosenColumns: z.array(columnSchema),
});
export const selectColumnsResponseSchema = z.object({
    chosenColumns: z.array(columnSchema),
});
export const addRowRequestSchema = z.object({
    rowData: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.date()])),
});
export const addRowResponseSchema = z.object({
    rowIndex: z.number(),
});
export const updateRowRequestSchema = z.object({
    columnName: z.string(),
    rowIndex: z.number().int().min(0),
    value: z.union([z.string(), z.number(), z.boolean(), z.date()]),
});
export const tableCreationSchema = z.object({
    id: z.string(),
    tableName: z.string().nullable(),
    originalInput: z.string(),
    recommendedColumns: z.array(columnSchema),
    chosenColumns: z.array(columnSchema),
    rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.date()]))),
    currentStep: z.string(),
    status: z.enum(['pending', 'processing', 'completed', 'failed']),
    errorMessage: z.string().optional(),
    retryCount: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const tableSummarySchema = z.object({
    id: z.string(),
    tableName: z.string().nullable(),
    originalInput: z.string(),
    currentStep: z.string(),
    status: z.enum(['pending', 'processing', 'completed', 'failed']),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const apiOperationResponseSchema = z.object({
    status: z.string(),
});
// TanStack Query mutation hook for creating tables
export const useCreateTable = (request) => useMutation({
    mutationFn: (data) => {
        // Validate the request data
        const validatedData = createTableRequestSchema.parse(data);
        return request({
            url: '/tables',
            method: 'POST',
            data: validatedData,
        }, createTableResponseSchema);
    },
});
// TanStack Query hook for fetching user's tables
export const useTables = (request) => useQuery({
    queryKey: ['tables'],
    queryFn: () => request({
        url: '/tables',
        method: 'GET',
    }, z.array(tableSummarySchema)),
});
// TanStack Query hook for fetching table details
export const useTable = (request, tableId) => useQuery({
    queryKey: ['table', tableId],
    queryFn: () => request({
        url: `/tables/${tableId}`,
        method: 'GET',
    }, tableCreationSchema),
    enabled: !!tableId,
    staleTime: 0,
});
// TanStack Query mutation hook for suggesting columns
export const useSuggestColumns = (request) => useMutation({
    mutationFn: (tableId) => {
        return request({
            url: `/tables/${tableId}/suggest-columns`,
            method: 'POST',
        }, apiOperationResponseSchema);
    },
});
// TanStack Query mutation hook for selecting columns
export const useSelectColumns = (request) => useMutation({
    mutationFn: ({ tableId, ...data }) => {
        // Validate the request data
        const validatedData = selectColumnsRequestSchema.parse(data);
        return request({
            url: `/tables/${tableId}/select-columns`,
            method: 'POST',
            data: validatedData,
        }, selectColumnsResponseSchema);
    },
});
// TanStack Query mutation hook for generating samples
export const useGenerateSamples = (request) => useMutation({
    mutationFn: (tableId) => {
        return request({
            url: `/tables/${tableId}/generate-samples`,
            method: 'POST',
        }, apiOperationResponseSchema);
    },
});
// Alias for better semantics when generating suggested rows
export const useGenerateSuggestedRows = useGenerateSamples;
// TanStack Query mutation hook for adding rows
export const useAddRow = (request) => useMutation({
    mutationFn: ({ tableId, ...data }) => {
        // Validate the request data
        const validatedData = addRowRequestSchema.parse(data);
        return request({
            url: `/tables/${tableId}/rows`,
            method: 'POST',
            data: validatedData,
        }, addRowResponseSchema);
    },
});
// TanStack Query mutation hook for updating rows
export const useUpdateRow = (request) => useMutation({
    mutationFn: ({ tableId, ...data }) => {
        // Validate the request data
        const validatedData = updateRowRequestSchema.parse(data);
        return request({
            url: `/tables/${tableId}/rows`,
            method: 'PUT',
            data: validatedData,
        });
    },
});
// TanStack Query mutation hook for deleting rows
export const useDeleteRow = (request) => useMutation({
    mutationFn: ({ tableId, rowIndex }) => {
        return request({
            url: `/tables/${tableId}/rows/${rowIndex}`,
            method: 'DELETE',
        });
    },
});
// TanStack Query mutation hook for deleting tables
export const useDeleteTable = (request) => useMutation({
    mutationFn: (tableId) => {
        return request({
            url: `/tables/${tableId}`,
            method: 'DELETE',
        });
    },
});
// TanStack Query mutation hook for retrying operations
export const useRetryOperation = (request) => useMutation({
    mutationFn: (tableId) => {
        return request({
            url: `/tables/${tableId}/retry`,
            method: 'POST',
        }, apiOperationResponseSchema);
    },
});
